import { ColumnDef } from "@tanstack/react-table";
import useDataTable from "hooks/useDataTable";
import { ClientEngagementSliceState, setClientEngagementCount, setClientEngagements, setClientEngagementSearchFields, deleteClientEnagement } from 'slices/business-development/client-engagement/reducer'
import useManual from "hooks/useManual"
import { ClientEngagement, IClientEngagementQueryParams } from "models/client-engagement";
import React, { useCallback, useEffect, useState } from "react";
import { createSelector } from "reselect";
import { useDispatch, useSelector } from "react-redux";
import DataTableTemplate from "Components/templates/data-table-template";
import { getClientEngagements, getClientEngagementSearchFields } from "slices/thunk"
import { deleteClientEngagement } from "slices/business-development/client-engagement/thunk"
import AreYouSureModal from "Components/organisms/are-you-sure-modal";
import SearchClientEngagement from "Components/organisms/forms/search-client-engagement-form";
import Client from "models/client";
import Employee from "models/employee";
import Opportunity from "models/opportunity";
import ActionButton from "Components/atoms/buttons/action-button";
import TableRowControlsContainer from "Components/atoms/containers/table-row-controls-container";
import IconButton from "Components/atoms/buttons/icon-button";
import { ProfileSliceState } from "slices/auth/profile/reducer";
import { navigateToCreateClientEngagement, navigateToEditClientEngagement } from "utils/navigator";
import withRouter from "Components/Common/withRouter";
import { PlusIcon, HelpIcon, PencilIcon, TrashIcon } from "Components/atoms/icons";

const selectClientEngagement = createSelector(
    (state: any) => state.ClientEngagement,
    ({
        clientEngagementCount,
        clientEngagements,
        employees,
        opportunities,
        clients
    }: ClientEngagementSliceState) => ({
        clientEngagementCount,
        clientEngagements,
        employees,
        opportunities,
        clients
    })
)

const selectProfile = createSelector(
    (state: any) => state.Profile,
    ({
        employee
    }: ProfileSliceState) => ({
        employee
    })
)



const DEFAULT_SEARCH: IClientEngagementQueryParams = {
    page: 0,
    limit: Number.MAX_SAFE_INTEGER,
    opportunityIds: [],
    createdByIds: [],
    clientIds: [],
}

const ClientEngagementPage = (props: any) => {
    document.title = "Client Engagement | IbexIQ";
    const { handleOpenManual } = useManual("business-development/client-engagement");
    const [query, setQuery] = useState<IClientEngagementQueryParams>(DEFAULT_SEARCH);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [focusedEngagementId, setFocusedEngagementId] = useState<string | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const dispatch: any = useDispatch();

    const {
        clientEngagementCount,
        clientEngagements,
        employees,
        opportunities,
        clients
    } = useSelector(selectClientEngagement)

    const { employee } = useSelector(selectProfile)


    const fetchClientEngagements = async (q: IClientEngagementQueryParams) => {
        setIsLoading(true)
        const clientEngagements = await getClientEngagements(q);

        if (!clientEngagements) {
            setIsLoading(false);
            return;
        }

        dispatch(setClientEngagements(clientEngagements.data))
        dispatch(setClientEngagementCount(clientEngagements.count))
        setIsLoading(false)
    }


    const handleChangeQuery = <K extends keyof IClientEngagementQueryParams>(
        key: K,
        value: IClientEngagementQueryParams[K]
    ) => {


        if (key !== 'page' && key !== 'limit') {
            query.page = 0;
            query.limit = Number.MAX_SAFE_INTEGER
        }
        const q = {
            ...query,
            [key]: value
        }
        setQuery(q);
    };

    const handleClearSearch = useCallback(() => {
        const q = { ...DEFAULT_SEARCH }
        setQuery(q);
        fetchClientEngagements(q)
    }, [setQuery, fetchClientEngagements]);

    const hanldeAddClientEngagement = () => navigateToCreateClientEngagement(props.router.navigate)
    const handleEditClientEngagement = (clientEngagement: ClientEngagement) => navigateToEditClientEngagement(props.router.navigate, clientEngagement.id)

    const handleDeleteClientEngagement = (clientEngagement: ClientEngagement) => {
        setFocusedEngagementId(clientEngagement.id);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = async () => {
        setIsDeleteModalOpen(false);
        setFocusedEngagementId(null);
    };

    const confirmDeleteClientEngagement = useCallback(async () => {
        setIsDeleting(true)
        if (!focusedEngagementId) return;
        const success = await deleteClientEngagement(focusedEngagementId);
        if (success) {
            dispatch(deleteClientEnagement(focusedEngagementId));
            handleCloseDeleteModal();
        }

        setIsDeleting(false)
    }, [focusedEngagementId]);

    const initializePage = async () => {

        if (
            employees.length === 0 ||
            opportunities.length === 0 ||
            clients.length === 0
        ) {
            const res = await getClientEngagementSearchFields()
            if (!res) {
                return;
            }
            dispatch(setClientEngagementSearchFields(res))
        }
        if (clientEngagements.length === 0) {
            await fetchClientEngagements(query)
        }
        setIsLoading(false)
    }

    // Load Client Engagements and search fields
    useEffect(() => {
        initializePage()
    }, [])


    const columns = React.useMemo<ColumnDef<ClientEngagement>[]>(() => [
        {
            header: "Client",
            id: "client",
            accessorFn: (row) => row.client?.name,
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            size: 100,
            cell: ({ row }) => {
                const isOwner = row.original.createdById === employee?.id
                return <TableRowControlsContainer
                    style={{ justifyContent: 'space-between' }}
                >
                    {row.original.client?.name}
                    {
                        isOwner &&
                        <div className='d-flex flex-row justify-content-end'>
                            <IconButton onClick={() => handleEditClientEngagement(row.original)}><PencilIcon /></IconButton>
                            <IconButton onClick={() => handleDeleteClientEngagement(row.original)}><TrashIcon /></IconButton>
                        </div>
                    }
                </TableRowControlsContainer>
            }
        },
        {
            header: "Created By",
            id: "date",
            accessorFn: (row) => row.createdBy?.commonName ?? row.createdBy?.firstName + " " + row.createdBy?.lastName,
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            size: 80,
            cell: ({ row }) => row.original.createdBy?.commonName ?? row.original.createdBy?.firstName + " " + row.original.createdBy?.lastName,
        },
        {
            header: "Opportunity",
            id: "opportunity",
            accessorFn: (row) => row.opportunity?.description,
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            size: 120,
            cell: ({ row }) =>
                row.original.opportunity?.description ?? "Not found",
        },
        {
            header: "Date Of Next Checkin",
            id: "dateCheckIn",
            accessorFn: (row) => row.dateOfNextCheckin ? row.dateOfNextCheckin.toString() : undefined,
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            size: 80,
            cell: ({ row }) => {
                if (row.original.dateOfNextCheckin) {
                    return row.original.dateOfNextCheckin.toString().split("T")[0]
                } else {
                    return "Not found"
                }
            }
        },
        {
            header: "Feedback",
            id: "feedback",
            accessorFn: (row) => row.notes ?? undefined,
            size: 200,
            meta: {
                getCellStyle: () => ({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }),
            },
            cell: ({ row }) =>
                row.original.notes ?? "Not found"
        }], []);


    const table = useDataTable({
        columns,
        data: clientEngagements,
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    return <DataTableTemplate
        stickyFoot={false}
        title={"Business Development"}
        breadcrumbItem={"Client Engagement"}
        table={table}
        columns={columns}
        isLoading={isLoading}
        rowCount={clientEngagementCount}
        modal={<AreYouSureModal
            isOpen={isDeleteModalOpen}
            onModalClose={handleCloseDeleteModal}
            modalTitle="Delete Client Engagement"
            handleAreYouSureAccept={confirmDeleteClientEngagement}
            handleAreYouSureDecline={handleCloseDeleteModal}
            areYouSureacceptText={isDeleting ? 'Deleting...' : 'Delete'}
            areYouSuredeclineText="Cancel"
        />}
        actions={[
            <ActionButton
                onClick={hanldeAddClientEngagement}
            >
                Add <PlusIcon />
            </ActionButton>,
            <ActionButton
                    onClick={handleOpenManual}
                >
                    Help <HelpIcon />
                </ActionButton>
        ]}
        filters={<SearchClientEngagement
            handleChangeQuery={handleChangeQuery}
            handleClearSearch={handleClearSearch}
            handleSearch={function () {
                fetchClientEngagements(query)
            }}
            clients={[{ value: '', label: 'All' }, ...clients.map((bA: Client) => ({ value: bA.id, label: bA.name }))]}
            createdBy={[{ value: '', label: 'All' }, ...employees.map((e: Employee) => ({ value: e.id, label: (e.commonName ?? e.firstName) + " " + e.lastName }))]}
            opportunities={[{ value: '', label: 'All' }, ...opportunities.map((o: Opportunity) => ({ value: o.id, label: o.description }))]}
            query={query}
            isSearching={isLoading}
        />}
    />

}

export default withRouter(ClientEngagementPage);